import { useState } from 'react';
import { companyCodeFromUrl } from '@/lib/storage';
import {
  Alert, Button, IconButton, InputAdornment, Link, Stack, TextField, Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import AuthShell from '@/components/layout/AuthShell';
import { resetPasswordSchema, type ResetPasswordFormValues } from '@/auth/schemas';
import { authApi } from '@/api/auth.api';
import { ApiError } from '@/lib/apiClient';
import { humanizeError } from '@/utils/errorMessage';

/**
 * Reset Password page — the destination of the emailed reset link
 * (/reset-password?token=...&tenant=...). The token + tenant come from the URL (never entered by the
 * user, never stored/logged); the form only collects the new password + confirmation. Submits to the
 * existing POST /api/auth/reset-password endpoint, which validates + consumes the token server-side.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const tenant = companyCodeFromUrl(params);
  // An invitation link (no password is ever emailed): the person verifies their email by choosing one.
  const invite = params.get('invite') === '1';

  const [error, setError] = useState<string | null>(null);
  const [invalidToken, setInvalidToken] = useState(false);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { control, handleSubmit } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: '', confirm: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (submitting) return; // guard against duplicate submits
    setError(null);
    setSubmitting(true);
    try {
      await authApi.resetPassword({
        token,
        newPassword: values.newPassword,
        confirmPassword: values.confirm,
        tenantCode: tenant,
      });
      setDone(true);
    } catch (err) {
      // Missing / invalid / expired / used token → the dedicated invalid-link state (Phase 11).
      if (err instanceof ApiError && err.errorCode === 'AUTH_INVALID_RESET_TOKEN') {
        setInvalidToken(true);
      } else {
        setError(humanizeError(err, 'We could not reset your password. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  });

  // ---- Invalid / missing / expired / used token state ----
  if (!token || invalidToken) {
    return (
      <AuthShell
        title={invite ? 'Invitation link invalid' : 'Reset link invalid'}
        subtitle={invite ? 'This invitation link is invalid or has expired.' : 'This password reset link is invalid or has expired.'}
      >
        <Stack spacing={2}>
          <Alert severity="error">
            {invite
              ? 'This invitation link is invalid or has expired. Links work once; ask your admin to resend the invitation.'
              : 'This password reset link is invalid or has expired. Reset links can only be used once and expire after a short time.'}
          </Alert>
          <Button component={RouterLink} to="/forgot-password" variant="contained" size="large" fullWidth>
            Request New Reset Link
          </Button>
          <Button component={RouterLink} to="/login" size="large" fullWidth>
            Back to Login
          </Button>
        </Stack>
      </AuthShell>
    );
  }

  // ---- Success state ----
  if (done) {
    return (
      <AuthShell
        title={invite ? 'Your account is ready' : 'Password Reset Successful'}
        subtitle={invite ? 'Your email is verified and your password is set.' : 'Your password has been changed successfully.'}
      >
        <Stack spacing={2}>
          <Alert severity="success">
            {invite ? 'Sign in with your email and the password you just set.' : 'You can now sign in with your new password.'}
          </Alert>
          <Button variant="contained" size="large" fullWidth onClick={() => navigate('/login', { replace: true })}>
            Go to Login
          </Button>
        </Stack>
      </AuthShell>
    );
  }

  // ---- Form ----
  return (
    <AuthShell
      title={invite ? 'Set your password' : 'Reset Password'}
      subtitle={invite ? 'Verify your email by choosing your own password.' : "Choose a strong password you haven't used before."}
      footer={<Link component={RouterLink} to="/login" variant="body2">← Back to Login</Link>}
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Controller
            name="newPassword"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="New Password"
                type={showNew ? 'text' : 'password'}
                fullWidth
                autoComplete="new-password"
                autoFocus
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? 'At least 8 characters, with an uppercase, a lowercase and a number.'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNew((v) => !v)} edge="end" size="small"
                        aria-label={showNew ? 'Hide new password' : 'Show new password'}>
                        {showNew ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="confirm"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Confirm Password"
                type={showConfirm ? 'text' : 'password'}
                fullWidth
                autoComplete="new-password"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end" size="small"
                        aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}>
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting}>
            {submitting ? 'Saving…' : invite ? 'Verify and set password' : 'Reset Password'}
          </Button>

          <Typography variant="caption" color="text.secondary" align="center">
            For your security, all active sessions are signed out after a password reset.
          </Typography>
        </Stack>
      </form>
    </AuthShell>
  );
}
